import { ReactNode } from "react";
import { cn } from "../../../lib/utils";


export interface MergedColumnHeader {
  label: ReactNode;
  startColumnIndex: number;
  endColumnIndex: number;
  className?: string;
}

export interface MergedHeadersSet {
  mergedColumns: MergedColumnHeader[];
}

export interface ReportTableColumn<DataType> {
  headerLabel: string;
  headerClassName?: string;
  getValue: (rowData: DataType) => ReactNode;
  columnCellClassName?: string;
}

export interface ReportTableRow<DataType> {
  key?: string | number;
  data: DataType;
  rowClassName?: string;
  rowCellClassName?: string;
}

export interface IReportTableProps<DataType> {
  columns: ReportTableColumn<DataType>[];
  mergedHeadersSets?: MergedHeadersSet[];
  rows: ReportTableRow<DataType>[];
  className?: string;
}

const renderMergedHeadersSet = (
  set: MergedHeadersSet,
  rowIndex: number,
  columnCount: number
): ReactNode => {
  const cells = [...set.mergedColumns].sort(
    (a, b) => a.startColumnIndex - b.startColumnIndex
  );

  const nodes: ReactNode[] = [];
  let cursor = 0;

  const pushGap = (until: number, key: string) => {
    if (until > cursor) {
      nodes.push(<th key={key} colSpan={until - cursor} aria-hidden />);
      cursor = until;
    }
  };

  cells.forEach((cell, i) => {
    pushGap(cell.startColumnIndex, `gap-${rowIndex}-${i}`);
    nodes.push(
      <th
        key={`cell-${rowIndex}-${i}`}
        colSpan={cell.endColumnIndex - cell.startColumnIndex + 1}
        className={cell.className}
      >
        {cell.label}
      </th>
    );
    cursor = cell.endColumnIndex + 1;
  });

  pushGap(columnCount, `gap-${rowIndex}-end`);

  return <tr key={`merged-${rowIndex}`}>{nodes}</tr>;
};

const InternalReportTable = <DataType,>({
  columns,
  mergedHeadersSets,
  rows,
  className,
}: IReportTableProps<DataType>) => (
  <div className={cn("w-full overflow-x-auto bg-white", className)}>
    <table className="w-full border-collapse">
      <thead>
        {mergedHeadersSets?.map((set, rowIndex) =>
          renderMergedHeadersSet(set, rowIndex, columns.length)
        )}
        <tr>
          {columns.map((column, columnIndex) => (
            <th key={columnIndex} className={column.headerClassName}>
              {column.headerLabel}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={row.key ?? rowIndex} className={row.rowClassName}>
            {columns.map((column, columnIndex) => (
              <td
                key={columnIndex}
                className={cn(column.columnCellClassName, row.rowCellClassName)}
              >
                {column.getValue(row.data)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default InternalReportTable;
